<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import RoomForm from './RoomForm.vue';
import RoomCard from './RoomCard.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmation = ref(false);
const selectedRoom = ref({});
const isSubmitting = ref(false);
const filterStatus = ref('all');
const searchQuery = ref('');
const presetParentId = ref(null);

const records = computed(() => getters['motorReservaRooms/getRecords'].value);
const uiFlags = computed(() => getters['motorReservaRooms/getUIFlags'].value);

// Imóveis principais (sem parent) e independentes
const rootRooms = computed(() => records.value.filter(r => !r.parent_id));

// Mapa de filhos por parent_id
const childrenMap = computed(() => {
  const map = {};
  records.value.forEach(r => {
    if (r.parent_id) {
      if (!map[r.parent_id]) map[r.parent_id] = [];
      map[r.parent_id].push(r);
    }
  });
  return map;
});

// Filtro aplicado nos roots
const filteredRoots = computed(() => {
  let result = rootRooms.value;
  if (filterStatus.value !== 'all') {
    result = result.filter(r => r.status === filterStatus.value);
  }
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase();
    result = result.filter(r => {
      const matchSelf =
        r.name.toLowerCase().includes(q) ||
        (r.room_number || '').toLowerCase().includes(q) ||
        (r.room_type || '').toLowerCase().includes(q) ||
        (r.external_ref || '').toLowerCase().includes(q);
      // Também mostra pai se algum filho bate no filtro
      const kids = childrenMap.value[r.id] || [];
      const matchChild = kids.some(
        c =>
          c.name.toLowerCase().includes(q) ||
          (c.room_number || '').toLowerCase().includes(q)
      );
      return matchSelf || matchChild;
    });
  }
  return result;
});

const parentRooms = computed(() => rootRooms.value);

const getChildren = roomId => childrenMap.value[roomId] || [];

// CRUD
const createRoom = async formData => {
  isSubmitting.value = true;
  try {
    await store.dispatch('motorReservaRooms/create', formData);
    useAlert(t('MOTOR_RESERVA.ROOMS.ADD.API.SUCCESS_MESSAGE'));
    showAddPopup.value = false;
    presetParentId.value = null;
  } catch (error) {
    useAlert(error?.message || t('MOTOR_RESERVA.ROOMS.ADD.API.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};

const updateRoom = async formData => {
  isSubmitting.value = true;
  try {
    await store.dispatch('motorReservaRooms/update', {
      id: selectedRoom.value.id,
      ...formData,
    });
    useAlert(t('MOTOR_RESERVA.ROOMS.EDIT.API.SUCCESS_MESSAGE'));
    showEditPopup.value = false;
  } catch (error) {
    useAlert(error?.message || t('MOTOR_RESERVA.ROOMS.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};

const deleteRoom = async () => {
  try {
    await store.dispatch('motorReservaRooms/delete', selectedRoom.value.id);
    useAlert(t('MOTOR_RESERVA.ROOMS.DELETE.API.SUCCESS_MESSAGE'));
    showDeleteConfirmation.value = false;
  } catch (error) {
    useAlert(
      error?.message || t('MOTOR_RESERVA.ROOMS.DELETE.API.ERROR_MESSAGE')
    );
  }
};

const openEdit = room => {
  selectedRoom.value = room;
  showEditPopup.value = true;
};

const openDelete = room => {
  selectedRoom.value = room;
  showDeleteConfirmation.value = true;
};

const openAddChild = parent => {
  presetParentId.value = parent.id;
  selectedRoom.value = {
    parent_id: parent.id,
    room_type: 'Unidade Habitacional',
  };
  showAddPopup.value = true;
};

const openAdd = () => {
  presetParentId.value = null;
  selectedRoom.value = {};
  showAddPopup.value = true;
};

const duplicateRoom = async room => {
  isSubmitting.value = true;
  try {
    const data = {
      motor_reserva_room: {
        name: room.name + ' (Cópia)',
        description: room.description || '',
        rich_description: room.rich_description || '',
        max_occupancy: room.max_occupancy || 2,
        max_adults: room.max_adults || 2,
        max_children: room.max_children || 0,
        min_occupancy: room.min_occupancy || 1,
        total_units: room.total_units || 1,
        active: room.active !== false,
        amenities_list: room.amenities_list || [],
        bed_types: room.bed_types || [],
        allows_smokers: room.allows_smokers || false,
        youtube_url: room.youtube_url || '',
        room_type: room.room_type || '',
        room_number: '',
        slogan: room.slogan || '',
        status: room.status || 'active',
        parent_id: room.parent_id || null,
        external_ref: null,
      },
    };
    await store.dispatch('motorReservaRooms/create', data);
    useAlert('Quarto duplicado com sucesso.');
  } catch (error) {
    useAlert(error?.message || 'Erro ao duplicar quarto.');
  } finally {
    isSubmitting.value = false;
  }
};

onBeforeMount(() => {
  store.dispatch('motorReservaRooms/get');
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base font-medium text-slate-800 dark:text-slate-100">
          {{ t('MOTOR_RESERVA.ROOMS.HEADER') }}
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-300">
          {{ t('MOTOR_RESERVA.ROOMS.DESCRIPTION') }}
        </p>
      </div>
      <Button
        :label="'+ ' + t('MOTOR_RESERVA.ROOMS.ADD.TITLE')"
        @click="openAdd"
      />
    </div>

    <!-- Filtros -->
    <div class="flex flex-wrap gap-3 items-center">
      <input
        v-model="searchQuery"
        type="text"
        class="flex-1 min-w-[200px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:border-woot-500 focus:outline-none"
        placeholder="Buscar por nome, número, tipo ou ref..."
      />
      <select
        v-model="filterStatus"
        class="rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:border-woot-500 focus:outline-none"
      >
        <option value="all">Todos os Status</option>
        <option value="active">Ativo</option>
        <option value="inactive">Inativo</option>
        <option value="sold">Vendido</option>
        <option value="rented">Locado</option>
      </select>
    </div>

    <!-- Loading -->
    <div
      v-if="uiFlags.isFetching"
      class="flex items-center justify-center py-8"
    >
      <span class="text-slate-500">{{
        t('MOTOR_RESERVA.COMMON.LOADING')
      }}</span>
    </div>

    <!-- Cards Hierárquicos -->
    <div v-else class="flex flex-col gap-3">
      <RoomCard
        v-for="room in filteredRoots"
        :key="room.id"
        :room="room"
        :children="getChildren(room.id)"
        :is-submitting="isSubmitting"
        @edit="openEdit"
        @delete="openDelete"
        @duplicate="duplicateRoom"
        @add-child="openAddChild"
        @edit-child="openEdit"
        @delete-child="openDelete"
      />
    </div>

    <p
      v-if="!uiFlags.isFetching && !filteredRoots.length"
      class="text-sm text-slate-500 text-center py-8"
    >
      Nenhum imóvel encontrado.
    </p>

    <!-- Add Modal -->
    <div
      v-if="showAddPopup"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div
        class="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <RoomForm
          :room="selectedRoom"
          :is-submitting="isSubmitting"
          :parent-rooms="parentRooms"
          @submit="createRoom"
          @close="
            showAddPopup = false;
            presetParentId = null;
          "
        />
      </div>
    </div>

    <!-- Edit Modal -->
    <div
      v-if="showEditPopup"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div
        class="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <RoomForm
          :room="selectedRoom"
          :is-submitting="isSubmitting"
          :parent-rooms="parentRooms"
          @submit="updateRoom"
          @close="showEditPopup = false"
        />
      </div>
    </div>

    <!-- Delete Confirmation -->
    <div
      v-if="showDeleteConfirmation"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div class="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-sm">
        <p class="text-sm text-slate-700 dark:text-slate-200 mb-4">
          {{ t('MOTOR_RESERVA.ROOMS.DELETE.CONFIRM') }}
        </p>
        <div class="flex gap-2 justify-end">
          <Button
            variant="secondary"
            :label="t('MOTOR_RESERVA.COMMON.CANCEL')"
            @click="showDeleteConfirmation = false"
          />
          <Button
            variant="danger"
            :label="t('MOTOR_RESERVA.COMMON.DELETE')"
            @click="deleteRoom"
          />
        </div>
      </div>
    </div>
  </div>
</template>
